import { fetchWithCSRF } from "@/helpers/common/csrf";
import clsx from "clsx";
import {
    Check,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Download,
    Search,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { TagHistoryEntry } from "./_types";

const API_URL = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;

const shieldTypeBadge: Record<string, { label: string; classes: string }> = {
    shield: {
        label: "Shield",
        classes:
            "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
    },
    shield_two: {
        label: "Shield II",
        classes:
            "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
    },
};

interface Pagination {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    has_next_page: boolean;
    has_previous_page: boolean;
}

export default function TagHistoryPage() {
    const [entries, setEntries] = useState<TagHistoryEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        page_size: 25,
        total_items: 0,
        total_pages: 1,
        has_next_page: false,
        has_previous_page: false,
    });
    const [completedFilter, setCompletedFilter] = useState<string>("false");
    const [shieldTypeFilter, setShieldTypeFilter] = useState<string>("");
    const [searchQuery, setSearchQuery] = useState("");
    const [completingAll, setCompletingAll] = useState(false);

    const fetchEntries = useCallback(
        async (page = 1) => {
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    page: String(page),
                    page_size: "25",
                    sort_by: "shield_number",
                    order: "asc",
                });
                if (completedFilter) params.set("completed", completedFilter);
                if (shieldTypeFilter)
                    params.set("shield_type", shieldTypeFilter);
                if (searchQuery) params.set("search", searchQuery);

                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/tag-history/?${params}`,
                );
                if (res.ok) {
                    const data = await res.json();
                    setEntries(data.results);
                    setPagination(data.pagination);
                }
            } catch (error) {
                console.error("Error fetching tag history:", error);
            } finally {
                setLoading(false);
            }
        },
        [completedFilter, shieldTypeFilter, searchQuery],
    );

    useEffect(() => {
        fetchEntries(1);
    }, [fetchEntries]);

    const handleComplete = useCallback(
        async (id: string) => {
            try {
                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/tag-history/${id}/complete`,
                    { method: "PATCH" },
                );
                if (res.ok) {
                    fetchEntries(pagination.page);
                }
            } catch (error) {
                console.error("Error completing tag history entry:", error);
            }
        },
        [fetchEntries, pagination.page],
    );

    const handleCompleteAll = useCallback(async () => {
        if (
            !window.confirm(
                "Are you sure you want to mark ALL pending tag history entries as completed?",
            )
        ) {
            return;
        }
        setCompletingAll(true);
        try {
            const res = await fetchWithCSRF(
                `${API_URL}/longhouse-api/village/tag-history/complete-all-pending`,
                { method: "PATCH" },
            );
            if (res.ok) {
                fetchEntries(1);
            }
        } catch (error) {
            console.error("Error completing all tag history entries:", error);
        } finally {
            setCompletingAll(false);
        }
    }, [fetchEntries]);

    const handleExport = useCallback(async () => {
        try {
            const res = await fetchWithCSRF(
                `${API_URL}/longhouse-api/village/tag-history/export`,
            );
            if (res.ok) {
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "pending_tag_history.xlsx";
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error("Error exporting tag history:", error);
        }
    }, []);

    return (
        <div className="mx-auto mt-4 w-full px-4">
            {/* Page Header */}
            <div className="mb-6 rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                            Tag History
                        </h1>
                        <p className="mt-2 text-slate-600 dark:text-slate-400">
                            Track tags earned and upgraded across all shields.
                            Mark entries as completed once acknowledged.
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={handleExport}
                            className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700"
                        >
                            <Download className="h-5 w-5" />
                            Export Pending
                        </button>
                        <button
                            onClick={handleCompleteAll}
                            disabled={completingAll}
                            className={clsx(
                                "flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white transition-colors",
                                completingAll
                                    ? "cursor-not-allowed bg-emerald-400"
                                    : "bg-emerald-600 hover:bg-emerald-700",
                            )}
                        >
                            <CheckCircle className="h-5 w-5" />
                            {completingAll
                                ? "Completing..."
                                : "Complete All Pending"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="mb-6 flex flex-wrap items-center gap-4">
                <div className="relative">
                    <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search by player name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="rounded-lg border border-slate-300 bg-white py-2.5 pr-4 pl-10 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
                    />
                </div>
                <select
                    value={completedFilter}
                    onChange={(e) => setCompletedFilter(e.target.value)}
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                >
                    <option value="">All Statuses</option>
                    <option value="false">Pending</option>
                    <option value="true">Completed</option>
                </select>
                <select
                    value={shieldTypeFilter}
                    onChange={(e) => setShieldTypeFilter(e.target.value)}
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                >
                    <option value="">All Shield Types</option>
                    <option value="shield">Shield</option>
                    <option value="shield_two">Shield II</option>
                </select>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                    {pagination.total_items} total entries
                </span>
            </div>

            {/* Table */}
            <div className="rounded-2xl border border-slate-300 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="border-brand h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" />
                    </div>
                ) : entries.length === 0 ? (
                    <div className="p-8 text-center">
                        <p className="text-slate-600 dark:text-slate-400">
                            No tag history entries found
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Shield #
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Player
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Shield Type
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Tag
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Prev Tier
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            New Tier
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Earned
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                    {entries.map((entry) => (
                                        <tr
                                            key={entry.id}
                                            className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="font-mono text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                    {entry.shield_number || "-"}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                                    {entry.player_name}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {(() => {
                                                    const badge =
                                                        shieldTypeBadge[
                                                            entry.shield_type
                                                        ] || {
                                                            label: entry.shield_type,
                                                            classes:
                                                                "bg-slate-100 text-slate-800",
                                                        };
                                                    return (
                                                        <span
                                                            className={clsx(
                                                                "rounded-md px-2.5 py-1 text-xs font-semibold",
                                                                badge.classes,
                                                            )}
                                                        >
                                                            {badge.label}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-slate-700 dark:text-slate-300">
                                                    {entry.tag_name}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-500 dark:text-slate-400">
                                                {entry.previous_tier}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                                                    {entry.new_tier}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-600 dark:text-slate-400">
                                                {new Date(
                                                    entry.earned_at,
                                                ).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {entry.completed ? (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900 dark:text-green-300">
                                                        <CheckCircle className="h-3 w-3" />
                                                        Completed
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900 dark:text-amber-300">
                                                        Pending
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right whitespace-nowrap">
                                                {!entry.completed ? (
                                                    <button
                                                        onClick={() =>
                                                            handleComplete(
                                                                entry.id,
                                                            )
                                                        }
                                                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700"
                                                    >
                                                        <Check className="h-3.5 w-3.5" />
                                                        Complete
                                                    </button>
                                                ) : (
                                                    <span className="text-sm text-slate-400">
                                                        —
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-700">
                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                    Page {pagination.page} of{" "}
                                    {pagination.total_pages} (
                                    {pagination.total_items} entries)
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() =>
                                            fetchEntries(pagination.page - 1)
                                        }
                                        disabled={!pagination.has_previous_page}
                                        className={clsx(
                                            "flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                                            pagination.has_previous_page
                                                ? "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                                                : "cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-600",
                                        )}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                        Previous
                                    </button>
                                    <button
                                        onClick={() =>
                                            fetchEntries(pagination.page + 1)
                                        }
                                        disabled={!pagination.has_next_page}
                                        className={clsx(
                                            "flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                                            pagination.has_next_page
                                                ? "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                                                : "cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-600",
                                        )}
                                    >
                                        Next
                                        <ChevronRight className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
